import React, { useState, useEffect } from 'react';
import { useUser } from '@/contexts/UserContext';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AlertTriangle, Database, Upload, Wand2, AlertCircle } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import EntityManagementTable from '../components/admin/EntityManagementTable';
import { Link } from 'react-router-dom';
import { createPageUrl } from '@/utils';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';

// Import all entities needed for the tabs
import { AccountFinal } from '@/api/entities';
import { PersonFinal } from '@/api/entities';
import { Procurement } from '@/api/entities';
import { PersonaActivity } from '@/api/entities';
import { MomentumScore } from '@/api/entities';
import { Deal } from '@/api/entities';
import { Playbook } from '@/api/entities';

const entityConfigs = [
    { name: "Accounts", entity: AccountFinal, columns: [{ key: 'account_name', header: 'Name' }, { key: 'country', header: 'Country' }] },
    { name: "People", entity: PersonFinal, columns: [{ key: 'first_name', header: 'First Name' }, { key: 'last_name', header: 'Last Name' }, { key: 'account_name', header: 'Account' }] },
    { name: "Procurement", entity: Procurement, columns: [{ key: 'title', header: 'Title' }, { key: 'institution', header: 'Institution' }] },
    { name: "Persona Activity", entity: PersonaActivity, columns: [{ key: 'full_name', header: 'Person' }, { key: 'summary', header: 'Activity' }] },
    { name: "Momentum Scores", entity: MomentumScore, columns: [{ key: 'institution_name', header: 'Institution' }, { key: 'summary', header: 'Summary' }] },
    { name: "Deals", entity: Deal, columns: [{ key: 'institution_name', header: 'Institution' }, { key: 'product_name', header: 'Product' }, { key: 'supplier', header: 'Supplier' }] },
    { name: "Playbooks", entity: Playbook, columns: [{ key: 'title', header: 'Title' }, { key: 'category', header: 'Category' }] },
];

export default function DataManagement() {
  const contextUser = useUser();
  const [isLoading, setIsLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    setIsLoading(false);
  }, [contextUser]);

  const handleUploadComplete = () => {
    // Force refresh of all entity tables by updating the key
    setRefreshKey(prev => prev + 1);
  };

  if (isLoading) {
    return (
        <div className="max-w-7xl mx-auto px-6 py-8">
            <Skeleton className="h-10 w-1/3 mb-4" />
            <Skeleton className="h-8 w-1/2 mb-8" />
            <Skeleton className="h-96 w-full" />
        </div>
    );
  }

  if (!contextUser) {
      return (
        <div className="max-w-7xl mx-auto px-6 py-8">
            <Skeleton className="h-10 w-1/3 mb-4" />
            <Skeleton className="h-8 w-1/2 mb-8" />
            <Skeleton className="h-96 w-full" />
        </div>
    );
  }

  if (contextUser?.role !== 'admin') {
      return (
        <div className="max-w-4xl mx-auto px-6 py-8 text-center">
            <AlertTriangle className="mx-auto h-12 w-12 text-yellow-400" />
            <h1 className="mt-4 text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl">Access Denied</h1>
            <p className="mt-4 text-base text-gray-500">You must be an administrator to access this page.</p>
        </div>
      );
  }

  return (
    <div className="bg-white min-h-screen">
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-[#0A1A2F]">Data Management</h1>
          <p className="text-lg text-[#5C6B7A]">Upload CSV data and bulk manage record visibility across the platform.</p>
        </div>
        
        <Card className="mb-8 border-blue-200 bg-blue-50/50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-blue-900">
              <Wand2 className="w-5 h-5 text-blue-600" />
              Tech Stack Data Update
            </CardTitle>
            <CardDescription className="text-blue-800/90">
              Click the button below to update account records with the latest tech stack information (LMS, SIS, CRM, ePortfolio products). This is a one-time process that will update existing accounts.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Link to={createPageUrl('TempDataUpdate')}>
              <Button className="bg-blue-600 hover:bg-blue-700">
                Go to Tech Stack Update Page
              </Button>
            </Link>
          </CardContent>
        </Card>

        <Card className="mb-8 border-green-200 bg-green-50/50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-green-900">
              <Upload className="w-5 h-5 text-green-600" />
              Account Fit Score CSV Upload
            </CardTitle>
            <CardDescription className="text-green-800/90">
              Upload your account fit scores using CSV or tab-separated data. Supports both account_id and account_name matching.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Link to={createPageUrl('TempAccountFitUpload')}>
              <Button className="bg-green-600 hover:bg-green-700">
                Go to Fit Score Upload Page
              </Button>
            </Link>
          </CardContent>
        </Card>

        <Alert className="mb-8">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            <strong>Note:</strong> The general CSV uploader is currently experiencing technical issues. Please use the dedicated upload pages above for reliable data uploads.
          </AlertDescription>
        </Alert>

        <Tabs defaultValue="accounts" className="mt-8">
            <TabsList>
                {entityConfigs.map(config => (
                    <TabsTrigger key={config.name} value={config.name.toLowerCase()}>{config.name}</TabsTrigger>
                ))}
            </TabsList>
            
            {entityConfigs.map(config => (
                <TabsContent key={config.name} value={config.name.toLowerCase()} className="mt-4">
                    <Card className="border-0 shadow-md">
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Database className="w-5 h-5 text-[#00C2C2]" />
                                Managing {config.name}
                            </CardTitle>
                            <CardDescription>Select records to set their visibility status.</CardDescription>
                        </CardHeader>
                        <CardContent>
                           <EntityManagementTable 
                                key={`${config.name}-${refreshKey}`}
                                entity={config.entity} 
                                entityName={config.name} 
                                columns={config.columns}
                            />
                        </CardContent>
                    </Card>
                </TabsContent>
            ))}
        </Tabs>
      </div>
    </div>
  );
}